import { API_BASE_URL, getStoredToken } from "./auth";

function getActiveTenantId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("activeTenantId");
}

function buildHeaders(withJson = false): Record<string, string> {
  const token = getStoredToken();
  const tenantId = getActiveTenantId();
  const headers: Record<string, string> = {};

  if (withJson) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (tenantId) headers["X-Tenant-ID"] = tenantId;

  return headers;
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const mergedHeaders: Record<string, string> = {
    ...buildHeaders(false),
    ...((init.headers as Record<string, string> | undefined) ?? {})
  };

  return fetch(`${API_BASE_URL}${normalizedPath}`, {
    ...init,
    headers: mergedHeaders
  });
}

export async function apiFetchJson<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  // Automatically set Content-Type: application/json when a JSON body is provided
  const hasBody = init.body !== undefined && init.body !== null;
  const hasContentType = init.headers && ('Content-Type' in (init.headers as Record<string, string>));

  if (hasBody && !hasContentType) {
    init = {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...((init.headers as Record<string, string>) ?? {}),
      },
    };
  }

  const response = await apiFetch(path, init);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }

  // Handle 204 No Content or empty responses
  if (response.status === 204) {
    return {} as T;
  }

  const text = await response.text();
  if (!text) return {} as T;

  try {
    return JSON.parse(text) as T;
  } catch (e) {
    console.error("Failed to parse JSON response", text);
    throw new Error("Invalid server response (not JSON)");
  }
}

export async function apiPostJson<T = unknown>(path: string, body: unknown): Promise<T> {
  return apiFetchJson<T>(path, {
    method: "POST",
    headers: buildHeaders(true),
    body: JSON.stringify(body)
  });
}

export async function apiPostForm<T = unknown>(path: string, formData: URLSearchParams): Promise<T> {
  const response = await apiFetch(path, {
    method: "POST",
    headers: {
      ...(buildHeaders(false)),
      "Content-Type": "application/x-www-form-urlencoded"
    },
    body: formData.toString()
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  const text = await response.text();
  if (!text) return {} as T;

  try {
    return JSON.parse(text) as T;
  } catch (e) {
    console.error("Failed to parse JSON response", text);
    throw new Error("Invalid server response (not JSON)");
  }
}
